/**
 * Fulfilment identity policy — doc 71 MNRQ-UX-03.
 *
 * ONE ANSWER TO "WHAT DOES THIS ORDER NEED FROM THE GUEST BEFORE IT EXISTS".
 *
 * Doc 70 §8 shipped a blanket rule: pickup, delivery and scheduled pre-orders
 * all required a signed-in account, on the argument that "nobody is in the
 * building". MNRQ-UX-03 reverses that for the modes where a contact is enough:
 *
 *   · pickup and scheduled pre-order  → guest-first. The guest gives a name and
 *     a phone number so staff can call if
 *     anything changes; no account is demanded. A no-show is still a merchant
 *     cost, but a name + reachable contact is what actually mitigates it — an
 *     account alone never did.
 *   · delivery                        → account-required, unchanged. A rider
 *     leaves the building with the food; a failed delivery with no verified
 *     contact has nobody to call, and the address history belongs to an
 *     account. Revisited by MNRQ-UX-04.
 *   · dine-in / counter               → nothing. The QR the guest scanned IS
 *     the identity; a login wall in front of a seated diner buys nothing.
 *
 * THE MERCHANT OVERRIDE. A business that genuinely needs mandatory identity
 * (high-value goods, deposit-heavy pre-orders, fraud history) can opt in via
 * branch metadata (`requires_account_for_collection`). The default is
 * guest-friendly, per doc 71: "Merchant configuration exists only if a
 * business genuinely needs mandatory identity."
 *
 * PURE. No React, no storage, no fetch — the server reads the merchant flag and
 * passes it in; the client passes its own knowledge (scanned QR, branch flag).
 * That is what keeps the button and the boundary on the same rule.
 *
 * @see lib/customer/order-context.ts — the mode vocabulary this builds on
 * @see app/api/public/orders/route.ts — the authoritative enforcement point
 */

import type { OrderMode } from '@/lib/customer/order-context';

export type FulfilmentPolicy = {
  /** A signed-in customer account is required before the order can exist. */
  requiresAccount: boolean;
  /** A name + reachable contact is required before the order can exist. */
  requiresContact: boolean;
  /** Why, for the copy that explains the gate before the guest hits it. */
  reason: 'delivery' | 'collection' | 'merchant_required' | null;
};

export type FulfilmentPolicyInput = {
  mode: OrderMode;
  /** A scanned seat/till QR. The QR is the identity — never gate that guest. */
  scannedQr?: boolean;
  /** Branch opt-in (metadata `requires_account_for_collection`). Default off. */
  merchantRequiresAccount?: boolean;
};

/** A contact that staff can call. Email addresses are not reachable by phone. */
export function isPhoneContact(value: string | null | undefined): boolean {
  const contact = value?.trim() ?? '';
  if (!contact || !/^\+?[\d\s().-]+$/.test(contact)) return false;
  const digits = contact.replace(/\D/g, '');
  return digits.length >= 7 && digits.length <= 15;
}

/**
 * The identity a fulfilment mode demands from the guest.
 *
 * `scannedQr` wins over everything: a guest who scanned a seat or till QR is
 * physically present, so neither an account nor a contact is demanded of them —
 * the counter-QR case sets the page's mode to `pickup` while the guest stands
 * at the till, and gating that guest would demand a sign-in from someone
 * already holding their wallet out.
 */
export function resolveFulfilmentPolicy(input: FulfilmentPolicyInput): FulfilmentPolicy {
  const { mode, scannedQr = false, merchantRequiresAccount = false } = input;

  if (scannedQr) {
    return { requiresAccount: false, requiresContact: false, reason: null };
  }

  switch (mode) {
    case 'delivery':
      return { requiresAccount: true, requiresContact: true, reason: 'delivery' };
    case 'pickup':
    case 'scheduled':
      return merchantRequiresAccount
        ? { requiresAccount: true, requiresContact: true, reason: 'merchant_required' }
        : { requiresAccount: false, requiresContact: true, reason: 'collection' };
    case 'dine_in':
    case 'counter':
    case 'browse':
    default:
      return { requiresAccount: false, requiresContact: false, reason: null };
  }
}

/**
 * The mode-only default: which modes demand an account when no merchant flag
 * and no QR context is known. Delivery is the only one left after MNRQ-UX-03.
 */
export function modeRequiresAccount(mode: OrderMode): boolean {
  return mode === 'delivery';
}

/** The mode-only default: which modes demand a name + reachable contact. */
export function modeRequiresContact(mode: OrderMode): boolean {
  return mode === 'pickup' || mode === 'delivery' || mode === 'scheduled';
}

/**
 * Read the merchant opt-in from a branch metadata record.
 *
 * `requires_account_for_collection` is the key; anything truthy opts in. The
 * value is deliberately a boolean, not a string — a merchant UI writes it, and
 * a stray "false" string must not read as true.
 */
export function readMerchantRequiresAccount(metadata: unknown): boolean {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return false;
  const value = (metadata as Record<string, unknown>).requires_account_for_collection;
  return value === true;
}
